import { useQuery } from '@apollo/client';
import React, { useState } from 'react';
import { StackShim as Stack } from '@fluentui/react-migration-v8-v9';
import { Button } from '@fluentui/react-components';
import { TimelineRegular, EditRegular } from '@fluentui/react-icons';
import { useHistory, useParams } from 'react-router-dom';
import {
  bodyContentContainer,
  iconButtonStyles,
} from '../../../../app/common/styles/CommonStyleObjects';
import {
  APPROVE_DIVIDENDS_REVIEW,
  DIVIDENDS_REVIEW_QUERY,
  REJECT_DIVIDENDS_REVIEW,
  ATTACH_DIVIDEND_REVIEW_DOCUMENT,
  CREATE_DIVIDEND_REVIEW_COMMENT,
  EDIT_DIVIDENDS_MUTATION,
} from '../../../../utils/statApi/CompanyFiscalYearsApi';
import IDividends from '../../../../utils/types/IDividends';
import LoadingErrorMessage from '../../../common/errorContent/LoadingErrorMessage';
import { ICompanyCodeParams, IDividendReviewParams } from '../../../common/ParamTypes';
import ReviewComponent from '../../../reviews/ReviewComponent';
import DividendsCardContent from './DividendsCardContent';
import FiscalYearDividendsEditPanel from '../FiscalYearDividendsEditPanel';
import CanAccess from '../../../common/canAccess/CanAccess';
import RejectButtonMode from '../../../reviews/RejectButtonMode';

const DividendsReview = (): JSX.Element => {
  const { dividendReviewId } = useParams<IDividendReviewParams>();
  const { companyCode } = useParams<ICompanyCodeParams>();
  const [isEditPanelOpen, setIsEditPanelOpen] = useState(false);
  const { data, error, loading, refetch } = useQuery(DIVIDENDS_REVIEW_QUERY, {
    fetchPolicy: 'cache-and-network',
    variables: { dividendReviewId: parseInt(dividendReviewId, 10) },
  });

  const history = useHistory();

  const closePanel = (): void => {
    setIsEditPanelOpen(false);
    refetch();
  };

  const renderActionButton = (): JSX.Element => {
    return (
      <Stack horizontal tokens={{ childrenGap: 20 }} wrap>
        {data?.dividendReview?.afsProcessId && (
          <>
            <Button
              appearance="transparent"
              title="Go to Process"
              aria-label="Go to Process"
              icon={<TimelineRegular color={iconButtonStyles.color} />}
              onClick={(): void => {
                history.push(
                  `/companies/${companyCode}/processes/annual-financial-statements/${data?.dividendReview?.afsProcessId}`,
                );
              }}
            >
              Go To Process
            </Button>
            <span>|</span>
          </>
        )}
        <CanAccess requestedAction="DividendInfo:Update">
          <Button
            appearance="transparent"
            title="Edit Dividends"
            aria-label="Edit Dividends"
            icon={<EditRegular color={iconButtonStyles.color} />}
            onClick={(): void => {
              setIsEditPanelOpen(true);
            }}
          >
            Edit Dividends
          </Button>
        </CanAccess>
      </Stack>
    );
  };

  const dividends: IDividends = data?.dividendReview?.dividends || {};

  return (
    <>
      <div className={`${bodyContentContainer} lightBackground`}>
        <LoadingErrorMessage error={error} loading={loading} />
        <DividendsCardContent
          dividends={dividends}
          isLoading={loading}
          actionButtons={renderActionButton()}
        />
        <br />
        <ReviewComponent
          review={data?.dividendReview?.review}
          approveMutation={APPROVE_DIVIDENDS_REVIEW}
          rejectMutation={REJECT_DIVIDENDS_REVIEW}
          loading={loading}
          createCommentMutation={CREATE_DIVIDEND_REVIEW_COMMENT}
          attachDocumentMutation={ATTACH_DIVIDEND_REVIEW_DOCUMENT}
          rejectButtonMode={RejectButtonMode.RequestChanges}
        />
      </div>
      {isEditPanelOpen && (
        <FiscalYearDividendsEditPanel
          dividends={dividends}
          closePanel={closePanel}
          mutation={EDIT_DIVIDENDS_MUTATION}
        />
      )}
    </>
  );
};

export default DividendsReview;
